import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNextGameAvailability, useMyNextGameReservation } from '../../hooks/useReservation';
import { useCountdown, formatCountdown } from '../../hooks/useCountdown';

interface NextGameProps {
  compact?: boolean;
}

export function NextGame({ compact = false }: NextGameProps) {
  const { t } = useTranslation('schedule');
  const { data: availability } = useNextGameAvailability();
  const myReservation = useMyNextGameReservation(availability?.gameDate);

  const dt = availability?.gameDate ? new Date(availability.gameDate + 'T12:00:00') : null;
  // Format parts separately to avoid the "г." suffix added by ru-RU with year:'numeric'
  const weekday   = dt?.toLocaleDateString('ru-RU', { weekday: 'long' }) ?? '…';
  const dayMonth  = dt?.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' }) ?? '…';
  const gameYear  = dt?.getFullYear() ?? '';
  const gameDateShort = dt ? `${dayMonth} ${gameYear}` : '…';

  if (compact) {
    return (
      <Link
        to="/schedule"
        className="card-vintage block p-4 hover:bg-mafia-smoke transition-colors group"
      >
        <p className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-1">
          {t('nextGame.label')}
        </p>
        <p className="font-display text-white font-semibold capitalize">{gameDateShort} · 18:00</p>
        <p className="text-mafia-paper-dim text-sm mt-0.5">Banyo Library</p>
      </Link>
    );
  }

  const isBooked = !!myReservation && myReservation.status !== 'CANCELLED';
  const isConfirmed = myReservation?.status === 'CONFIRMED';
  const spotsLeft = availability?.availableSpots ?? null;
  const reservationOpen = availability?.isOpen ?? true;
  const opensAt = availability?.opensAt ?? '';
  const countdown = useCountdown(opensAt);

  return (
    <div className="card-vintage p-8 text-center h-full flex flex-col justify-between">
      {/* Top label */}
      <div>
        <p className="font-mono text-xs text-mafia-gold tracking-[0.3em] uppercase mb-4">
          ◆ {t('nextGame.label')} ◆
        </p>

        <p className="font-mono text-2xl text-mafia-gold tracking-widest mb-1 capitalize">
          {weekday}
        </p>
        <h2 className="font-display text-3xl md:text-4xl font-bold text-mafia-paper mb-1 leading-tight">
          {dayMonth} {gameYear}
        </h2>

        <p className="font-mono text-mafia-gold text-2xl tracking-widest mb-5">18:00</p>

        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px bg-mafia-border" />
          <span className="text-mafia-gold/50 text-xs">◆</span>
          <div className="flex-1 h-px bg-mafia-border" />
        </div>

        <p className="font-display text-xl text-mafia-gold font-semibold mb-1">
          Banyo Library
        </p>
        <p className="text-mafia-muted text-sm font-mono mb-4">
          284 St Vincents Rd, Banyo QLD 4014
        </p>

        {/* Live spots counter */}
        {availability && (
          <p className="font-mono text-xs text-mafia-paper-dim mb-6">
            {spotsLeft === 0 ? (
              <span className="text-mafia-crimson">Мест нет</span>
            ) : (
              <>
                Свободно:{' '}
                <span className={spotsLeft !== null && spotsLeft <= 3 ? 'text-amber-400' : 'text-emerald-400'}>
                  {spotsLeft} / {availability.totalSpots}
                </span>
              </>
            )}
          </p>
        )}
      </div>

      {/* CTA */}
      {isBooked ? (
        <div className="mx-auto w-full max-w-xs">
          <div className={`border py-3 px-4 font-mono text-xs tracking-widest text-center ${
            isConfirmed
              ? 'border-emerald-400/40 text-emerald-400 bg-emerald-400/5'
              : 'border-amber-400/40 text-amber-400 bg-amber-400/5'
          }`}>
            {isConfirmed ? '✓ Вы записаны на эту игру' : '⏳ Ожидание подтверждения оплаты'}
          </div>
        </div>
      ) : !reservationOpen && availability ? (
        <div className="mx-auto w-full max-w-xs text-center space-y-1">
          <p className="font-mono text-xs text-mafia-muted tracking-widest uppercase">
            Бронирование откроется
          </p>
          <p className="font-mono text-xs text-mafia-paper-dim capitalize">
            {new Date(availability.opensAt).toLocaleDateString('ru-RU', {
              weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Australia/Brisbane',
            })}
            {' в '}
            {new Date(availability.opensAt).toLocaleTimeString('ru-RU', {
              hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Brisbane',
            })}
          </p>
          {!countdown.expired && (
            <p className="font-mono text-mafia-gold text-sm tracking-widest pt-1">
              {formatCountdown(countdown.days, countdown.hours, countdown.minutes, countdown.seconds)}
            </p>
          )}
        </div>
      ) : (
        <Link
          to="/reserve"
          className="btn-gold-solid mx-auto w-full max-w-xs block text-center py-4"
        >
          {t('nextGame.reserve')}
        </Link>
      )}
    </div>
  );
}
